import { skipToken } from '@apollo/client/react'
import type { ButtonProps, TooltipProps } from '@mui/material'
import { Button, Tooltip } from '@mui/material'
import { useMemo } from 'react'
import FluentChat24Filled from '~icons/fluent/chat-24-filled'
import { openSignModal } from 'src/components/Account/utils'
import { openMembershipRequiredDialog } from 'src/components/Membership/dialogs'
import type { UIButtonProps } from 'src/components/ui/Button'
import { UIButton } from 'src/components/ui/Button'
import Text from 'src/components/ui/Text'
import { useMyInfo } from 'src/services/apollo/auth'
import { useGetMedia } from 'src/services/apollo/media'
import { useMyMembership } from 'src/services/apollo/membership'
import { EmbeddedRoutes } from 'src/utils/embeddedRoute'
import { useEmbeddedNavigate } from 'src/utils/hooks/useEmbedded'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { userHasMembership } from 'src/utils/user'
import { createWorkspace } from '../Common/utils'

export const StartChatEditingButton = ({
  variant = 'filled',
  color = 'theme',
  className = 'gap-0.5',
  ...rest
}: Omit<UIButtonProps, 'onPress' | 'disabled' | 'busy' | 'icon'>) => {
  return (
    <ChatWorkspaceCreatorButton
      trackingArgs={{ location: 'start-button' }}
      variant={variant}
      color={color}
      className={className}
      icon={<FluentChat24Filled className="size-4" />}
      {...rest}
    >
      <Text i18nKey="generation:chat-editing.workspace.creator.buttons.create" />
    </ChatWorkspaceCreatorButton>
  )
}

export const StartChatEditingButtonLarger = ({
  variant = 'filled',
  color = 'theme',
  className = 'gap-0.5',
  ...rest
}: Omit<UIButtonProps, 'onPress' | 'disabled' | 'busy' | 'icon'>) => {
  return (
    <ChatWorkspaceCreatorButton
      trackingArgs={{ location: 'start-button' }}
      variant={variant}
      color={color}
      className={className}
      icon={<FluentChat24Filled className="size-5" />}
      {...rest}
    >
      <Text i18nKey="generation:chat-editing.workspace.creator.buttons.create-larger" />
    </ChatWorkspaceCreatorButton>
  )
}

export type ChatWorkspaceCreatorButtonProps = {
  initialImageId?: string
  trackingArgs?: {
    location: string
    modelId?: string
  }
} & Omit<UIButtonProps, 'onPress' | 'disabled' | 'busy'>

export const ChatWorkspaceCreatorButton = ({
  initialImageId,
  children,
  trackingArgs,
  ...rest
}: ChatWorkspaceCreatorButtonProps) => {
  return (
    <ChatWorkspaceCreatorActionWrapper
      initialImageId={initialImageId}
      redirectToCreator={true}
      trackingArgs={trackingArgs}
      component={({ handleClick, isLoading, isHandlingClick }) => (
        <UIButton
          busy={isHandlingClick}
          onPress={handleClick}
          disabled={isLoading}
          {...rest}
        >
          {children}
        </UIButton>
      )}
    />
  )
}

export type ChatWorkspaceCreatorToolProps = {
  initialImageId?: string
  tooltipProps: Omit<TooltipProps, 'children'>
  trackingArgs?: {
    location: string
    modelId?: string
  }
} & Omit<ButtonProps, 'onClick'>

export const ChatWorkspaceCreatorTool = ({
  initialImageId,
  children,
  tooltipProps,
  trackingArgs,
  ...rest
}: ChatWorkspaceCreatorToolProps) => {
  return (
    <ChatWorkspaceCreatorActionWrapper
      initialImageId={initialImageId}
      redirectToCreator={false}
      redirectNewPage={true}
      trackingArgs={trackingArgs}
      component={({ handleClick, isLoading, isHandlingClick }) => (
        <Tooltip {...tooltipProps}>
          <Button
            disabled={isLoading}
            loading={isHandlingClick}
            onClick={handleClick}
            {...rest}
          >
            {children}
          </Button>
        </Tooltip>
      )}
    />
  )
}

type ChatWorkspaceCreatorActionWrapperProps = {
  initialImageId?: string
  redirectToCreator?: boolean
  redirectNewPage?: boolean
  component: ({
    handleClick,
    isLoading,
    isHandlingClick,
  }: {
    handleClick: () => void
    isLoading: boolean
    isHandlingClick: boolean
  }) => JSX.Element
  trackingArgs?: {
    location: string
    modelId?: string
  }
}

const ChatWorkspaceCreatorActionWrapper = ({
  initialImageId,
  redirectToCreator,
  redirectNewPage,
  component,
  trackingArgs,
}: ChatWorkspaceCreatorActionWrapperProps) => {
  const { data: media, loading: isLoadingMedia } = useGetMedia(
    initialImageId ?? skipToken,
  )
  const { data: membershipInfo, loading: isLoadingMembership } =
    useMyMembership()
  const { data: userInfo, loading: isLoadingUserInfo } = useMyInfo()

  const hasValidMembership = useMemo(
    () => userHasMembership(membershipInfo),
    [membershipInfo],
  )
  const isLoading = useMemo(
    () => isLoadingMedia || isLoadingMembership || isLoadingUserInfo,
    [isLoadingMedia, isLoadingMembership, isLoadingUserInfo],
  )
  const embeddedNavigate = useEmbeddedNavigate()

  const [isHandlingClick, handleClick] = useLoadingCallback(async () => {
    track('chat_editing_click', trackingArgs)

    if (!userInfo) {
      openSignModal()
      return
    }

    if (!hasValidMembership) {
      openMembershipRequiredDialog('chat-editing')
      return
    }

    if (redirectToCreator) {
      embeddedNavigate(EmbeddedRoutes.chatEditingWorkspaceCreator(media?.id), {
        newPage: redirectNewPage,
      })
      return
    }

    track('new_workspace_click')

    // Create workspace directly if media is available
    if (media?.id) {
      const workspaceId = await createWorkspace(media.id, undefined)

      if (workspaceId) {
        embeddedNavigate(EmbeddedRoutes.chatEditingWorkspace(workspaceId), {
          newPage: redirectNewPage,
        })
      } else {
        console.error('Failed to create chat workspace')
      }
    }
  }, [hasValidMembership, userInfo, location, redirectToCreator, media])

  return component({
    handleClick,
    isLoading,
    isHandlingClick,
  })
}
